/**
 * Process an array of items with a concurrency limit to avoid overwhelming
 * the system with too many parallel operations.
 *
 * The items share one {@link createConcurrencyLimiter} limiter of their own, so the cap covers this
 * list only.
 *
 * @param items - Array of items to process
 * @param processor - Async function that processes each item
 * @param concurrency - Maximum number of concurrent operations (default: 10); a positive integer
 * @returns Promise that resolves to an array of results in the same order as input, or rejects with
 * the first failure, wrapped with the failing item's index
 * @throws RangeError when `concurrency` is not a positive integer
 */
export async function processWithConcurrencyLimit<T, R>(
  items: T[],
  processor: (item: T) => Promise<R>,
  concurrency: number = 10,
): Promise<R[]> {
  const runLimited = createConcurrencyLimiter(concurrency);

  return Promise.all(
    items.map((item, index) =>
      runLimited(async () => {
        try {
          return await processor(item);
        } catch (error) {
          throw new Error(`Error processing item at index ${index}: ${error}`);
        }
      }),
    ),
  );
}

/** Runs one task once a slot is free; see {@link createConcurrencyLimiter}. */
export type ConcurrencyLimiter = <R>(task: () => Promise<R>) => Promise<R>;

/**
 * Creates a limiter that keeps at most `concurrency` tasks running at once across every caller that
 * shares it. Use it when several independent groups of requests start together and the cap has to
 * hold for all of them, which {@link processWithConcurrencyLimit} cannot do because it limits one
 * list at a time.
 *
 * A finished task hands its slot straight to the oldest waiting task, so a task started in between
 * cannot push the running count past the cap.
 *
 * A task must not call the limiter it is running under: once every slot is held by a task waiting
 * on its own nested call, none of them can finish.
 *
 * @param concurrency - Maximum number of tasks running at the same time; a positive integer
 * @returns A function that runs a task inside the shared limit and resolves or rejects with it
 * @throws RangeError when `concurrency` is not a positive integer, since no task could ever start
 */
export function createConcurrencyLimiter(concurrency: number): ConcurrencyLimiter {
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError(`Concurrency limit must be a positive integer, received ${concurrency}`);
  }

  const waitingTasks: Array<() => void> = [];
  let runningCount = 0;

  return async <R>(task: () => Promise<R>): Promise<R> => {
    if (runningCount < concurrency) runningCount++;
    else await new Promise<void>((resolve) => waitingTasks.push(resolve));

    try {
      return await task();
    } finally {
      const nextTask = waitingTasks.shift();
      if (nextTask) nextTask();
      else runningCount--;
    }
  };
}
